// src/components/SimpleFormComponents/SimpleCnpj.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Form, Modal } from 'antd';
import { useEffect, useMemo, useRef, useCallback } from 'react';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import CpfCnpjInput from '../JarvislyComponents/CpfCnpjInput';
import { returnOnlyNumbers } from 'utils/helpers';
import { isValidCnpj } from 'utils/validation';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import translateX from 'utils/translateX';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import entitiesService from 'services/entitiesService';
import addressService from 'services/address/addressService';
import useZustand from 'hooks/useZustand';
import { useNavigate } from 'react-router-dom';
import { searchCnpjExternal } from 'services/cnpj-external/factory';
import { cleanPhoneNumber, derivePhoneType } from 'services/cnpj-external/cnpjUniversalPojo';
import appService from 'services/appService';
import { createImportToken, buildUrlWithToken } from 'utils/crossProfileImport';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleCnpj = ({
  name,
  subdocumentField,
  disabled,
  onBlur,
  searchCnpjByApi = false, // If true, search external APIs (BrasilAPI, CNPJ.ws) for auto-fill
  unique = false, // If true, search for existing entity with same CNPJ
  silentSearch = false, // If true, suppress all appService messages/notifications during CNPJ search
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    locked,
    getFormCnpjState,
    setFormCnpjState,
    setFormZipState, // needed to set ZIP state after CNPJ API fills address
    fieldsWithError,
    disabledFields,
    handleCnpjChange,
    handleCnpjBlur,
    data,
    form,
    setUniqueEntity,
    reloadEntirePage, // to determine how to handle unique entity loading
    formTmpState, // consume to trigger re-render when cnpj state changes
    removeFromFieldsWithError, // needed to clear validation errors after CNPJ API fills form
    goFocus, // needed to focus on 'name' field after loading existing entity
  } = useSimpleFormContext();

  const navigate = useNavigate();

  // Get zustand module store for unique entity handling
  const storedModuleConfig = useZustand('module', 'config');
  const storedCurrentPage = useZustand('module', 'currentPage');

  const [modal, contextHolder] = Modal.useModal();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // read value from Form state so it reacts to typing, paste and programmatic setFieldsValue
  const currentValueRaw = Form.useWatch(fullFieldName, form);
  // Handle both string and object values (e.g., { value: '123', countryCode: 'BR' })
  // When form stores CNPJ as object from external API, extract the .value property
  const currentValue =
    typeof currentValueRaw === 'object' && currentValueRaw !== null
      ? currentValueRaw.value
      : currentValueRaw;

  const dataValueRaw = getValueFromFlattenObj(data, flattenFieldName);
  // Also handle object values in initial data
  const dataValue =
    typeof dataValueRaw === 'object' && dataValueRaw !== null ? dataValueRaw.value : dataValueRaw;
  // Read cnpjState directly from formTmpState to ensure re-render when it changes
  const cnpjState = formTmpState?.cnpj?.[flattenFieldName] || null;
  const runOnce = useRef(false);

  // Ref to store the previous CNPJ value before unique search
  // Used to restore if user cancels the modal
  const previousCnpjRef = useRef(null);

  const {
    customValidationStatus: baseStatus,
    customValidationMessage: baseMessage,
    handleChange,
  } = useCustomValidationStatus(
    {
      ...props,
      ...elementProps,
      // force 'validating' while API lookup is running
      loading: !!cnpjState?.searching,
    },
    isValidCnpj
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // Lock CNPJ field if entity is persisted AND was enriched via external API
  // This prevents changing the primary identifier of an enriched entity
  const isEnrichedAndPersisted = data?._id && data?.dataEnrichment?.isEnriched === true;
  const isDisabled =
    disabled || locked || disabledFields?.includes(flattenFieldName) || isEnrichedAndPersisted;

  // Calculate final status based on priorities
  const { finalStatus, finalMessage } = useMemo(() => {
    const currentCnpj = returnOnlyNumbers(currentValue);
    const savedCnpj = returnOnlyNumbers(dataValue);

    // Priority 1: Validating (searching CNPJ)
    if (cnpjState?.searching) {
      return { finalStatus: 'validating', finalMessage: '' };
    }

    // Priority 2: Error from context (API failure or invalid CNPJ)
    const ctxMessageKey = fieldsWithError?.[flattenFieldName];
    if (ctxMessageKey) {
      return { finalStatus: 'error', finalMessage: translateX(ctxMessageKey) };
    }

    // Priority 3: Empty field - no validation display (required check happens on submit)
    if (!currentCnpj) {
      return { finalStatus: '', finalMessage: '' };
    }

    // Priority 4: Invalid CNPJ format or API Failure
    if (currentCnpj && !isValidCnpj(currentCnpj)) {
      return { finalStatus: 'error', finalMessage: baseMessage || '' };
    }

    // Priority 5: Success - current value matches saved value
    if ((savedCnpj && currentCnpj === savedCnpj) || cnpjState?.apiFailure) {
      return { finalStatus: 'success', finalMessage: '' };
    }

    // Priority 6: Valid but different from saved (or new entry)
    if (currentCnpj && isValidCnpj(currentCnpj)) {
      return { finalStatus: 'success', finalMessage: '' };
    }

    // Fallback to base status
    return { finalStatus: baseStatus || '', finalMessage: baseMessage || '' };
  }, [
    currentValue,
    fieldsWithError,
    flattenFieldName,
    cnpjState,
    baseStatus,
    baseMessage,
    dataValue,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (runOnce.current) return;

    const currentCnpj = returnOnlyNumbers(currentValue);
    const savedCnpj = returnOnlyNumbers(dataValue);

    // Only set lastSearchFound if the current value matches the saved value
    // (i.e., form loaded with existing data from database)
    if (finalStatus === 'success' && savedCnpj && currentCnpj === savedCnpj) {
      setFormCnpjState(flattenFieldName, { lastSearchFound: currentCnpj });
      runOnce.current = true;
    }
  }, [finalStatus, currentValue, dataValue, flattenFieldName, setFormCnpjState]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // Ref to track if we need to clear state on next blur (debounced state clearing)
  const needsClearStateRef = useRef(false);

  const handleCnpjChangeWrapper = useCallback(
    ev => {
      const value = ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '';

      // PERFORMANCE FIX: Only call handleChange for validation feedback
      // This is the minimum required for visual feedback during typing
      handleChange(ev);

      // PERFORMANCE FIX: Remove field from error state only (lightweight operation)
      // Don't update formCnpjState on every keystroke - defer to blur
      handleCnpjChange?.({
        ev,
        ...elementProps,
        value,
        subdocumentField,
      });

      // PERFORMANCE FIX: Mark that state needs to be cleared on blur
      // instead of clearing on every keystroke
      needsClearStateRef.current = true;
    },
    [handleChange, handleCnpjChange, elementProps, subdocumentField]
  );

  /**
   * Search for existing entity by CNPJ (unique validation)
   * Shows modal if entity found, allowing user to load existing data
   *
   * Two modes based on reloadEntirePage prop:
   * 1. reloadEntirePage=true: Navigate to entity document URL and refresh
   * 2. reloadEntirePage=false: Merge entity data into form and zustand store
   */
  const handleUniqueSearch = useCallback(
    async (cnpjValue, parentOnBlur) => {
      const cleanCnpj = returnOnlyNumbers(cnpjValue);

      // Skip if CNPJ is invalid
      if (!isValidCnpj(cleanCnpj)) {
        setUniqueEntity?.(null);
        parentOnBlur?.();
        return;
      }

      // Skip if CNPJ is the same as current data (editing existing document)
      const currentDataCnpj = returnOnlyNumbers(data?.cnpj?.value);
      if (cleanCnpj === currentDataCnpj) {
        parentOnBlur?.();
        return;
      }

      // Skip if this CNPJ was already searched (avoid re-fetching on every blur)
      if (cnpjState?.lastSearchFound === cleanCnpj) {
        parentOnBlur?.();
        return;
      }

      // NOTE: previousCnpjRef is now set in handleCnpjBlurWrapper BEFORE clearing state
      // This ensures we capture the last successfully searched CNPJ value

      try {
        // Show loading message
        if (!silentSearch) {
          appService.message({
            flag: 'i',
            text: 'checking_document_duplicate',
            key: 'checkDocumentDuplicate',
            duration: 0,
          });
        }

        // Search for existing entity with this CNPJ
        const foundEntity = await entitiesService.searchByDocument({
          key: 'cnpj',
          value: cleanCnpj,
        });

        // Destroy loading message
        if (!silentSearch) appService.messageDestroy('checkDocumentDuplicate');

        if (foundEntity) {
          const entityName = foundEntity.name || foundEntity.fullName || 'Empresa';

          // Track user action (used by afterClose to focus on appropriate field)
          let userAction = null; // 'confirmed' | 'canceled'

          // Show modal asking if the user wants to load an existing entity
          modal.confirm({
            title: translateX('document_already_exists'),
            content: translateX('document_already_exists_message', { name: entityName }),
            okText: translateX('yes_load_data'),
            cancelText: translateX('no_keep_current'),
            // Disable default focus restoration to prevent focus going to wrong field
            focusTriggerAfterClose: false,
            // Focus on appropriate field after modal closes
            afterClose: () => {
              if (userAction === 'canceled') {
                // User canceled - focus back on CNPJ field
                elementRef?.current?.focus?.();
              } else if (userAction === 'confirmed') {
                // User confirmed - focus on 'name' field (next logical field)
                goFocus?.('name');
              }
            },
            onOk: () => {
              if (reloadEntirePage) {
                // ─────────────────────────────────────────────────────────────
                // CASE 1: reloadEntirePage=true
                // Navigate to entity document URL with the found entity's _id
                // ─────────────────────────────────────────────────────────────
                const moduleUrl = storedModuleConfig?.url;
                const pageEndpoint = storedCurrentPage?.routing?.endpoint || '';

                if (moduleUrl && foundEntity._id) {
                  // Replace :_id in endpoint with the found entity's _id
                  const newEndpoint = pageEndpoint.replace(':_id', foundEntity._id);
                  let newUrl = `${moduleUrl}${newEndpoint}`;

                  // Create cross-profile import token to bypass entityProfile filter
                  // This allows loading an entity from a different profile (e.g., customer → company)
                  const currentFilter =
                    storedCurrentPage?.pagination?.filter ||
                    (storedCurrentPage?.api?.query?.filter
                      ? JSON.parse(storedCurrentPage.api.query.filter)
                      : null);

                  const targetProfile = currentFilter?.entityProfile || [];
                  const sourceProfile = foundEntity.dataProfile || foundEntity.entityProfile || [];

                  // Only add token if the entity doesn't already belong to the target profile
                  const needsImportToken =
                    targetProfile.length > 0 && !targetProfile.some(p => sourceProfile.includes(p));

                  if (needsImportToken) {
                    const token = createImportToken({
                      entityId: foundEntity._id,
                      sourceProfile,
                      targetProfile,
                    });
                    newUrl = buildUrlWithToken(newUrl, token);
                  }

                  // Navigate to the new URL (this will trigger a new GET for the entity)
                  navigate(newUrl, { replace: true });

                  // Clear unique entity since we're navigating to a new document
                  setUniqueEntity?.(null);
                }
              } else {
                // ─────────────────────────────────────────────────────────────
                // CASE 2: reloadEntirePage=false
                // Merge entity data into form and zustand store
                // The entity's _id becomes entityId (not the document's _id)
                // ─────────────────────────────────────────────────────────────

                // Get current form values to preserve specific fields
                const currentFormValues = form.getFieldsValue(true);

                // Prepare entity data for form (exclude _id and _metadata)
                const { _id: entityId, _metadata, ...entityFields } = foundEntity;

                // Build merged data: entity fields + entityId reference
                // Preserve avatar, backColor, foreColor from current form
                const mergedFormData = {
                  ...entityFields,
                  entityId, // Reference to the entity document
                  // Preserve these fields from current subscription
                  avatar: currentFormValues?.avatar,
                  backColor: currentFormValues?.backColor,
                  foreColor: currentFormValues?.foreColor,
                };

                // Update form with merged data
                form.setFieldsValue(mergedFormData);

                // FIX: Set ZIP state to prevent unnecessary CEP and GPS searches
                // when user interacts with address fields after loading existing entity
                if (entityFields?.address && setFormZipState) {
                  const addressData = entityFields.address;
                  const zipFieldName = 'address.zip';
                  const gpsHash =
                    addressData.zip && addressData.address1
                      ? `${addressData.zip}|${addressData.address1}|${addressData.number || ''}`
                      : null;

                  setFormZipState(zipFieldName, {
                    searching: false,
                    apiFailure: false,
                    lastSearchFound: addressData.zip,
                    data: addressData,
                    ...(gpsHash &&
                      addressData.addressCoordinates?.length && { lastGpsSearched: gpsHash }),
                  });
                }

                // Clear validation errors for fields filled by loading existing entity
                const filledFields = [
                  'name',
                  'fullName',
                  'phone.value',
                  'email.value',
                  'foundedAt',
                  'address.zip',
                  'address.address1',
                  'address.number',
                  'address.city',
                  'address.region',
                  'address.countryCode',
                  'fiscalData.stateRegistration',
                  'fiscalData.moreStateRegistrations',
                ];
                filledFields.forEach(field => removeFromFieldsWithError?.(field));

                // NOTE: We intentionally do NOT update the zustand store here!
                // Updating the store triggers a re-fetch of the document from the backend,
                // which overwrites the form with the OLD data (causing the revert issue).
                // The form.setFieldsValue is sufficient to update the form values.
                // The uniqueEntity state will be used in handleFinish to get entityId.

                // Save unique entity reference for use in handleFinish
                // This allows handleFinish to restore the entity's metadata if needed
                setUniqueEntity?.({
                  _id: entityId,
                  documentKey: 'cnpj',
                  documentValue: cleanCnpj,
                  data: foundEntity,
                });

                // Mark as confirmed so afterClose focuses on 'name' field
                userAction = 'confirmed';
              }

              // Mark this CNPJ as searched to prevent re-fetching on every blur
              setFormCnpjState?.(flattenFieldName, {
                lastSearchFound: cleanCnpj,
              });

              // NOTE: Do NOT call parentOnBlur() here!
              // parentOnBlur is the handleExternalApiSearch callback which:
              // 1. Sets entityId: null (for NEW entities from external API)
              // 2. Clears uniqueEntity state
              // This would overwrite the entityId and uniqueEntity we just set above.
              // parentOnBlur should only be called when entity is NOT found in database.
            },
            onCancel: () => {
              // Mark as canceled so afterClose knows to focus on CNPJ field
              userAction = 'canceled';

              // User chose NOT to load existing entity
              // Restore the previous CNPJ value (or empty if new document)
              const previousValue = previousCnpjRef.current;
              const previousValueClean = returnOnlyNumbers(previousValue);

              // Restore the CNPJ field to its previous value
              form.setFieldsValue({
                cnpj: previousValue ? { value: previousValue } : { value: '' },
              });

              // Clear unique entity state
              setUniqueEntity?.(null);

              // Restore CNPJ search state to previous value
              // This prevents re-searching when user blurs on the restored CNPJ
              setFormCnpjState?.(flattenFieldName, {
                searching: false,
                found: false,
                data: null,
                // Restore lastSearchFound to previous value (or null if empty)
                lastSearchFound: previousValueClean || null,
              });

              // NOTE: Focus is handled by afterClose callback to ensure
              // it happens AFTER the modal animation completes
              // Do NOT call parent onBlur if user cancels
            },
          });
        } else {
          // No existing entity found - clear unique entity state
          setUniqueEntity?.(null);

          // Mark this CNPJ as searched to prevent re-fetching on every blur
          setFormCnpjState?.(flattenFieldName, {
            lastSearchFound: cleanCnpj,
          });

          parentOnBlur?.();
        }
      } catch (error) {
        if (!silentSearch) appService.messageDestroy('checkDocumentDuplicate');
        setUniqueEntity?.(null);

        // Mark this CNPJ as searched even on error to prevent re-fetching
        setFormCnpjState?.(flattenFieldName, {
          lastSearchFound: cleanCnpj,
        });

        parentOnBlur?.();
      }
    },
    [
      data?.cnpj?.value,
      form,
      modal,
      setUniqueEntity,
      reloadEntirePage,
      storedModuleConfig,
      storedCurrentPage,
      navigate,
      setFormCnpjState,
      setFormZipState,
      flattenFieldName,
      elementRef,
      cnpjState?.lastSearchFound,
      removeFromFieldsWithError,
      goFocus,
      silentSearch,
    ]
  );

  /**
   * Search CNPJ via external APIs (BrasilAPI, CNPJ.ws)
   * and auto-fill form with company data
   */
  const handleExternalApiSearch = useCallback(
    async cnpjValue => {
      const cleanCnpj = returnOnlyNumbers(cnpjValue);

      // Skip if CNPJ is invalid
      if (!isValidCnpj(cleanCnpj)) {
        return;
      }

      // Skip if CNPJ is the same as current data (editing existing document)
      const currentDataCnpj = returnOnlyNumbers(data?.cnpj?.value);
      if (cleanCnpj === currentDataCnpj) {
        return;
      }

      // Skip if this CNPJ was already searched externally (avoid re-fetching on every blur)
      if (cnpjState?.lastSearchFound === cleanCnpj) {
        return;
      }

      // When silentSearch=true use a no-op message handle to avoid touching appService
      const msg = silentSearch
        ? { update: () => {}, close: () => {} }
        : await appService.message({
            flag: 'i',
            text: 'searching_cnpj_external',
            key: 'searchCnpjExternal',
            duration: 0,
          });

      try {
        // Show loading message
        setFormCnpjState?.(flattenFieldName, { searching: true });

        // Search external APIs
        const result = await searchCnpjExternal(cleanCnpj);

        // Check if search was successful
        if (result?.data?.success) {
          const pojo = result.data;

          // Get current form values to preserve specific fields
          const currentFormValues = form.getFieldsValue(true);

          // Transform POJO Universal to form values
          // Extract primary and secondary activities from POJO
          const primaryActivity = pojo.activities?.find(a => a.isPrimary);
          const secondaryActivities = pojo.activities?.filter(a => !a.isPrimary) || [];

          // ─────────────────────────────────────────────────────────────────
          // CONTACTS: Transform and mark first phone/email as primary
          // ─────────────────────────────────────────────────────────────────
          const processedContacts = transformContactsWithPrimary(pojo.contacts);

          const formData = {
            // IMPORTANT: CNPJ = company by definition
            // Set entityType to 'company' to override any default 'n/a' value
            entityType: 'company',
            dataOrigin: 'new_data',

            // Company names
            name: pojo.name || currentFormValues?.name,
            fullName: pojo.fullName || currentFormValues?.fullName,

            // Dates
            foundedAt: pojo.foundedAt || currentFormValues?.foundedAt,

            // CNPJ document
            cnpj: {
              value: pojo.documents?.[0]?.value || cleanCnpj,
              countryCode: 'BR',
            },

            // Primary phone - PRESERVE existing value, only fill if empty
            // API contacts go to 'contacts' array instead of replacing primary fields
            phone: (() => {
              // If user already has a phone, preserve it
              if (currentFormValues?.phone?.value) {
                return currentFormValues.phone;
              }
              // Otherwise, use the first phone from API
              const phoneContact = pojo.contacts?.find(
                c => c.contactType === 'phone' || c.contactType === 'mobile'
              );
              if (phoneContact) {
                return {
                  value: phoneContact.value,
                  countryCode: phoneContact.countryCode || 'BR',
                };
              }
              return currentFormValues?.phone;
            })(),

            // Primary email - PRESERVE existing value, only fill if empty
            // API contacts go to 'contacts' array instead of replacing primary fields
            email: (() => {
              // If user already has an email, preserve it
              if (currentFormValues?.email?.value) {
                return currentFormValues.email;
              }
              // Otherwise, use the first email from API
              const emailContact = pojo.contacts?.find(c => c.contactType === 'email');
              if (emailContact) {
                return {
                  value: emailContact.value,
                };
              }
              return currentFormValues?.email;
            })(),

            // ─────────────────────────────────────────────────────────────────
            // REGISTRATION STATUS (situação cadastral)
            // ─────────────────────────────────────────────────────────────────
            registrationStatus: pojo.registrationStatus
              ? {
                  date: pojo.registrationStatus.date || null,
                  code: pojo.registrationStatus.status
                    ? String(pojo.registrationStatus.status)
                    : null,
                  description: pojo.registrationStatus.statusText || null,
                }
              : currentFormValues?.registrationStatus,

            // Registration status reason (motivo da situação)
            registrationStatusReason: pojo.registrationStatus?.reason
              ? {
                  code: null,
                  description: pojo.registrationStatus.reason,
                }
              : currentFormValues?.registrationStatusReason,

            // ─────────────────────────────────────────────────────────────────
            // ACTIVITIES (CNAE - atividades econômicas)
            // ─────────────────────────────────────────────────────────────────
            mainActivity: primaryActivity
              ? {
                  code: primaryActivity.code,
                  description: primaryActivity.description,
                }
              : currentFormValues?.mainActivity,

            additionalActivities:
              secondaryActivities.length > 0
                ? secondaryActivities.map(a => ({
                    code: a.code,
                    description: a.description,
                  }))
                : currentFormValues?.additionalActivities,

            // ─────────────────────────────────────────────────────────────────
            // HOLDERS (sócios/quadro societário)
            // ─────────────────────────────────────────────────────────────────
            holders:
              pojo.holders?.length > 0
                ? pojo.holders.map(h => ({
                    name: h.name || '',
                    cpf: h.cpf || '',
                    qualification: h.qualification || '',
                    sinceAt: h.sinceAt || null,
                    legal: !!h.legalRepresentative,
                    ageRange: null,
                  }))
                : currentFormValues?.holders,

            // ─────────────────────────────────────────────────────────────────
            // COMPANY SIZE, SHARE CAPITAL & LEGAL NATURE
            // ─────────────────────────────────────────────────────────────────
            companySize: pojo.size
              ? {
                  code: null,
                  description: pojo.size,
                }
              : currentFormValues?.companySize,

            shareCapital: pojo.shareCapital ?? currentFormValues?.shareCapital,

            // ─────────────────────────────────────────────────────────────────
            // SEGMENT (derived from CNAE section - ISIC Rev.4)
            // ─────────────────────────────────────────────────────────────────
            segment: pojo.segment || currentFormValues?.segment,

            legalNature: pojo.legalNature
              ? {
                  code: pojo.legalNatureCode || null,
                  description: pojo.legalNature,
                }
              : currentFormValues?.legalNature,

            // ─────────────────────────────────────────────────────────────────
            // FISCAL DATA (dados fiscais)
            // ─────────────────────────────────────────────────────────────────
            fiscalData: {
              ...currentFormValues?.fiscalData,
              // Main CNAE for fiscal identification
              mainCnae: pojo.fiscalData?.mainCnae || currentFormValues?.fiscalData?.mainCnae,
              // Tax regime (Simples Nacional, etc.)
              taxRegime: pojo.fiscalData?.taxRegime || currentFormValues?.fiscalData?.taxRegime,
              // State Registration (IE - Inscrição Estadual) - get from stateRegistrations array
              // Prefer active registration, fallback to first one (primary IE)
              stateRegistration: (() => {
                if (currentFormValues?.fiscalData?.stateRegistration) {
                  return currentFormValues.fiscalData.stateRegistration;
                }
                if (pojo.stateRegistrations?.length > 0) {
                  const activeIE =
                    pojo.stateRegistrations.find(ie => ie.isActive) || pojo.stateRegistrations[0];
                  return activeIE?.value || null;
                }
                return null;
              })(),
              // Additional State Registrations (IEs for other states)
              // All IEs except the primary one go here
              moreStateRegistrations: (() => {
                if (currentFormValues?.fiscalData?.moreStateRegistrations?.length > 0) {
                  return currentFormValues.fiscalData.moreStateRegistrations;
                }
                if (pojo.stateRegistrations?.length > 1) {
                  // Get primary IE to exclude from the list
                  const primaryIE =
                    pojo.stateRegistrations.find(ie => ie.isActive) || pojo.stateRegistrations[0];
                  // Return all IEs except the primary one
                  return pojo.stateRegistrations
                    .filter(ie => ie.value !== primaryIE?.value)
                    .map(ie => ({
                      stateCode: ie.stateCode || '',
                      value: ie.value || '',
                      isActive: ie.isActive ?? true,
                      updatedAt: ie.updatedAt || null,
                    }));
                }
                return [];
              })(),
              // NFSe configuration - auto-fill CNAE
              nfse: {
                ...currentFormValues?.fiscalData?.nfse,
                cnae: pojo.fiscalData?.mainCnae || currentFormValues?.fiscalData?.nfse?.cnae,
              },
            },

            // Preserve these fields from current form
            avatar: currentFormValues?.avatar,
            backColor: currentFormValues?.backColor,
            foreColor: currentFormValues?.foreColor,

            // IMPORTANT: Clear entityId since this is a NEW entity from external API
            // (not found in database via unique search)
            // The entity will be created in handleFinish before saving the subscription
            entityId: null,

            // DATA ENRICHMENT: Track that this entity was enriched via external API
            // This will be used to lock the CNPJ field when editing persisted entities
            dataEnrichment: {
              isEnriched: true,
              enrichedAt: new Date().toISOString(),
              provider: result.resolvedName || 'external', // Provider that returned the data
              status: 'found',
            },

            // ─────────────────────────────────────────────────────────────────
            // CONTACTS: All contacts with first phone/email marked as primary
            // ─────────────────────────────────────────────────────────────────
            contacts:
              processedContacts.length > 0 ? processedContacts : currentFormValues?.contacts,
          };

          // Clear unique entity state since we're creating a new entity
          setUniqueEntity?.(null);

          // Update form with fetched data (except address - will be set after CEP lookup)
          form.setFieldsValue(formData);

          // Fetch full address data via CEP API to get all schema fields
          const mainAddress = pojo.addresses?.[0];
          // Track final address data for ZIP state update
          let finalAddressData = null;
          let finalGpsHash = null;

          if (mainAddress?.zip) {
            try {
              // Call CEP API to get full decorated address (with metadata, additional info, etc.)
              const cepResult = await addressService.getBrazilCep(mainAddress.zip);

              if (cepResult?.case === 3 && cepResult?.data) {
                const cepAddress = cepResult.data;

                // Merge CNPJ address data with CEP data
                // CNPJ provides: number, address2 (complement)
                // CEP provides: address1, neighborhood, city, cityCode (IBGE), region, metadata, additional info
                // NOTE: CNPJ's cityCode may be SIAFI (4 digits) - always use CEP's ibgeCode (7 digits) for NFe/NFCe
                const mergedAddress = {
                  ...cepAddress,
                  // Override with CNPJ data where available (more specific)
                  number: mainAddress.number || '',
                  address2: mainAddress.address2 || cepAddress.address2 || '',
                  // Use ibgeCode from CEP lookup (IBGE format required for NFe/NFCe cMun field)
                  cityCode: cepAddress.addressAdditionalInfo?.ibgeCode || cepAddress.cityCode || '',
                  countryCode: mainAddress.countryCode || 'BR',
                };

                // Update form with merged address
                form.setFieldsValue({ address: mergedAddress });
                finalAddressData = mergedAddress;

                // Fetch GPS coordinates
                if (mergedAddress.address1 && mergedAddress.city && mergedAddress.region) {
                  try {
                    const gpsResult = await addressService.getGpsCoordinates({
                      address: mergedAddress.address1,
                      number: mergedAddress.number || '',
                      city: mergedAddress.city,
                      region: mergedAddress.region,
                    });

                    if (gpsResult?.case === 3 && gpsResult?.data) {
                      const [latitude, longitude] = gpsResult.data;
                      const addressWithCoords = {
                        ...mergedAddress,
                        addressCoordinates: [latitude, longitude],
                      };
                      form.setFieldsValue({ address: addressWithCoords });
                      finalAddressData = addressWithCoords;
                      // Create GPS hash to prevent re-fetching
                      finalGpsHash = `${mergedAddress.zip || ''}|${mergedAddress.address1 || ''}|${mergedAddress.number || ''}`;
                    }
                  } catch (gpsError) {
                    console.warn('[SimpleCnpj] GPS lookup failed:', gpsError);
                  }
                }
              } else {
                // CEP lookup failed - use CNPJ address data directly
                // Prefer ibgeCode if available, otherwise use cityCode (may be SIAFI)
                const cnpjAddress = {
                  zip: mainAddress.zip,
                  address1: mainAddress.address1 || '',
                  address2: mainAddress.address2 || '',
                  number: mainAddress.number || '',
                  neighborhood: mainAddress.neighborhood || '',
                  city: mainAddress.city || '',
                  cityCode:
                    mainAddress.addressAdditionalInfo?.ibgeCode || mainAddress.cityCode || '',
                  region: mainAddress.region || '',
                  countryCode: mainAddress.countryCode || 'BR',
                  addressMetadata: {
                    countryCode: 'BR',
                    foundByApi: true,
                    manual: false,
                    apiFailure: false,
                  },
                };
                form.setFieldsValue({ address: cnpjAddress });
                finalAddressData = cnpjAddress;
              }
            } catch (cepError) {
              console.warn('[SimpleCnpj] CEP lookup failed:', cepError);
              // Use CNPJ address data as fallback
              // Prefer ibgeCode if available, otherwise use cityCode (may be SIAFI)
              const fallbackAddress = {
                zip: mainAddress.zip,
                address1: mainAddress.address1 || '',
                address2: mainAddress.address2 || '',
                number: mainAddress.number || '',
                neighborhood: mainAddress.neighborhood || '',
                city: mainAddress.city || '',
                cityCode: mainAddress.addressAdditionalInfo?.ibgeCode || mainAddress.cityCode || '',
                region: mainAddress.region || '',
                countryCode: mainAddress.countryCode || 'BR',
              };
              form.setFieldsValue({ address: fallbackAddress });
              finalAddressData = fallbackAddress;
            }

            // FIX: Set ZIP state to prevent unnecessary CEP and GPS searches
            // when user interacts with address fields after CNPJ API fill
            if (finalAddressData && setFormZipState) {
              const zipFieldName = 'address.zip'; // Standard address field path
              setFormZipState(zipFieldName, {
                searching: false,
                apiFailure: false,
                lastSearchFound: mainAddress.zip,
                data: finalAddressData,
                // Prevent GPS re-fetch if coordinates were already fetched
                ...(finalGpsHash && { lastGpsSearched: finalGpsHash }),
              });
            }
          }

          // Mark search as complete
          setFormCnpjState?.(flattenFieldName, {
            searching: false,
            lastSearchFound: cleanCnpj,
            apiFailure: false,
          });

          // Clear validation errors for fields filled by CNPJ API
          // This prevents "required" errors from showing for fields that were just filled
          const filledFields = [
            'name',
            'fullName',
            'phone.value',
            'email.value',
            'foundedAt',
            'address.zip',
            'address.address1',
            'address.number',
            'address.city',
            'address.region',
            'address.countryCode',
            'fiscalData.stateRegistration',
            'fiscalData.moreStateRegistrations',
          ];
          filledFields.forEach(field => removeFromFieldsWithError?.(field));

          // Show success notification
          msg.update({
            flag: 's',
            text: 'cnpj_found_by_api',
          });
        } else {
          // Search failed (not found or API error) - let user fill manually
          setFormCnpjState?.(flattenFieldName, {
            searching: false,
            apiFailure: true,
            lastSearchFound: cleanCnpj, // Mark as searched to prevent re-fetching
          });

          // Show notification informing user to fill data manually
          msg.update({
            flag: 'w',
            text: 'cnpj_not_found_title',
          });
        }
      } catch (error) {
        msg.close();

        setFormCnpjState?.(flattenFieldName, {
          searching: false,
          apiFailure: true,
          lastSearchFound: cleanCnpj, // Mark as searched to prevent re-fetching
        });

        // Show notification informing user to fill data manually
        if (!silentSearch) {
          appService.notification({
            key: 'cnpjApiErrorNotificationKey',
            flag: 'w',
            title: translateX('cnpj_api_error_title'),
            description: translateX('cnpj_api_error_description'),
            duration: 8,
          });
        }
      }
    },
    [
      data?.cnpj?.value,
      form,
      flattenFieldName,
      setFormCnpjState,
      setFormZipState,
      cnpjState?.lastSearchFound,
      removeFromFieldsWithError,
      silentSearch,
    ]
  );

  const handleCnpjBlurWrapper = async ev => {
    const value = ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '';
    const cleanCnpj = returnOnlyNumbers(value);

    // Capture the last successfully searched CNPJ BEFORE clearing state
    // This will be used to restore the field if user cancels the unique search modal
    const lastSearchedBeforeClear = cnpjState?.lastSearchFound;

    // PERFORMANCE FIX: Clear deferred state changes from onChange
    // This runs once on blur instead of on every keystroke
    if (needsClearStateRef.current) {
      needsClearStateRef.current = false;

      // Only clear state if the CNPJ actually changed from the last searched value
      // This prevents clearing when form.setFieldsValue programmatically sets the value
      const valueActuallyChanged =
        !lastSearchedBeforeClear || lastSearchedBeforeClear !== cleanCnpj;

      if (valueActuallyChanged) {
        // Store the previous CNPJ value BEFORE clearing state
        // This allows restoring it if user cancels the unique search modal
        previousCnpjRef.current = lastSearchedBeforeClear || data?.cnpj?.value || null;

        // Clear unique entity when CNPJ changes
        if (unique) {
          setUniqueEntity?.(null);
        }

        // Clear lastSearchFound when CNPJ changes to allow new search
        setFormCnpjState?.(flattenFieldName, {
          lastSearchFound: null,
          apiFailure: false,
        });

        // Destroy any existing notification
        appService.notificationDestroy('cnpjApiErrorNotificationKey');
      }
    }

    // Skip if CNPJ is invalid
    if (!isValidCnpj(cleanCnpj)) {
      return;
    }

    // Create a function for calling handleCnpjBlur from context
    const callContextBlur = () => {
      handleCnpjBlur?.({
        cnpj: elementProps,
        ev,
        value,
        subdocumentField,
      });
    };

    // Flow:
    // 1. If unique=true: search internal database first
    //    - If found: show modal (handled by handleUniqueSearch)
    //    - If NOT found AND searchCnpjByApi=true: search external APIs
    // 2. If unique=false AND searchCnpjByApi=true: search external APIs directly
    // NOTE: When unique=true, we don't call handleCnpjBlur (callContextBlur) because
    // handleUniqueSearch already handles the entity lookup, avoiding duplicate getByField calls
    if (unique) {
      await handleUniqueSearch(value, async () => {
        // This callback is called when no entity is found internally
        if (searchCnpjByApi) {
          await handleExternalApiSearch(value);
        }
      });
    } else if (searchCnpjByApi) {
      await handleExternalApiSearch(value);
      callContextBlur();
    }
    // else: No unique validation, no external API search
    // Just validate format (already done above) - don't call any API
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Show tooltip when field is disabled because entity was enriched via API
  const enrichedTooltip = isEnrichedAndPersisted
    ? translateX('entity_enriched_via_api')
    : props.tooltip;

  return (
    <>
      {contextHolder}
      <SimpleFormItem
        {...props}
        name={fullFieldName}
        elementProps={elementProps}
        customValidationStatus={finalStatus}
        customValidationMessage={finalMessage}
        tooltip={enrichedTooltip}
        // IMPORTANT: keep Form.Item feedback off to avoid AntD injecting suffix dynamically
        showFeedback={false}
      >
        <CpfCnpjInput
          ref={elementRef}
          id={flattenFieldName}
          onBlur={handleCnpjBlurWrapper}
          onChange={handleCnpjChangeWrapper}
          disabled={isDisabled}
          status={finalStatus}
          loading={cnpjState?.searching || false}
          profile="cnpj"
          // provide status & loading for static suffix rendering inside input
        />
      </SimpleFormItem>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCnpj;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Transform contacts array from CNPJ API and mark first phone/email as primary
 * Also ensures phone numbers are corrected (old mobile missing "9") and phoneType is derived
 * @param {Array} contacts - Raw contacts array from API
 * @returns {Array} Processed contacts with isPrimary set correctly
 */
function transformContactsWithPrimary(contacts) {
  if (!contacts || !Array.isArray(contacts) || contacts.length === 0) {
    return [];
  }

  // Track if we've already set primary for each type
  let hasPhonePrimary = false;
  let hasEmailPrimary = false;

  return contacts.map(contact => {
    const contactType = contact.contactType || 'other';
    const isPhone = contactType === 'phone' || contactType === 'mobile';
    const isEmail = contactType === 'email';

    // Determine if this contact should be primary
    let isPrimary = false;

    if (isPhone && !hasPhonePrimary) {
      isPrimary = true;
      hasPhonePrimary = true;
    } else if (isEmail && !hasEmailPrimary) {
      isPrimary = true;
      hasEmailPrimary = true;
    }

    // For phone contacts: fix old mobile numbers and derive correct phoneType
    const cleanedValue = isPhone ? cleanPhoneNumber(contact.value) : (contact.value || '');
    const phoneType = isPhone ? derivePhoneType(cleanedValue) : (contact.phoneType || contact.type || null);

    // Transform to standard contact structure
    return {
      contactType: isPhone ? 'phone' : contactType, // Normalize 'mobile' to 'phone'
      value: cleanedValue,
      countryCode: contact.countryCode || 'BR',
      phoneType,
      note: contact.note || '',
      isPrimary,
      hasWhatsApp: contact.hasWhatsApp || false,
    };
  });
}
